import useCustomFieldsHook from "@/hooks/use-custom-fields.hook";
import useEmployeeCustomFieldsHook from "@/hooks/use-employee-custom-fields.hook";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@skailer-com/uikit";
import { Ref, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { HireStep, useHireContext } from "../hire-context";
import { getHirePackageColumns, HireFieldValues } from "../hire-form";
import { HireCompensationLines, HireCompensationLinesHandle } from "./HireCompensationLines";

export interface HireAfterProbationPackageHandle {
  validate: () => Promise<boolean>;
}

export function HireAfterProbationPackage({
  ref,
  disabled,
}: {
  ref?: Ref<HireAfterProbationPackageHandle>;
  disabled: boolean;
}) {
  const { employee, onboarding, setOnboarding, errors, defaultCurrency } = useHireContext();
  const { fields } = useSystemTableConfig(ApiTableTypes.employee_compensations);
  const columns = useMemo(() => getHirePackageColumns(fields), [fields]);
  const formFields = useMemo(
    () => columns.filter((field) => field.type !== ApiFieldTypes.employee_compensation_lines),
    [columns]
  );
  const hasLinesColumn = formFields.length !== columns.length;
  const { getZodSchemaForField } = useCustomFieldsHook();
  const { getEmployeeEditFieldByType } = useEmployeeCustomFieldsHook(
    formFields.map((field) => field.type)
  );
  const schema = z.looseObject(
    Object.assign({}, ...formFields.map((field) => getZodSchemaForField(field)))
  );
  const form = useForm<HireFieldValues>({
    resolver: zodResolver(schema) as unknown as Resolver<HireFieldValues>,
    defaultValues: onboarding.compensation_columns,
  });
  const { subscribe, setError, trigger } = form;
  const linesRef = useRef<HireCompensationLinesHandle>(null);
  const serverErrors = errors[HireStep.onboarding];

  useEffect(
    () =>
      subscribe({
        formState: { values: true },
        callback: ({ values }) =>
          setOnboarding((current) => ({ ...current, compensation_columns: values })),
      }),
    [subscribe, setOnboarding]
  );
  useEffect(() => {
    for (const field of formFields) {
      const message = serverErrors?.[field.slug];
      if (message) setError(field.slug, { type: "server", message });
    }
  }, [formFields, serverErrors, setError]);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const isLinesValid = (await linesRef.current?.validate()) ?? true;
      const isColumnsValid = await trigger();
      return isLinesValid && isColumnsValid;
    },
  }));

  const lines = (
    <HireCompensationLines
      key="lines"
      ref={linesRef}
      value={onboarding.compensation}
      onChange={(compensation) => setOnboarding((current) => ({ ...current, compensation }))}
      required
      defaultCurrency={defaultCurrency}
      serverError={serverErrors?.compensation_after_probation}
      disabled={disabled}
    />
  );

  return (
    <Stack className={disabled ? "opacity-60 pointer-events-none" : undefined}>
      {!hasLinesColumn && lines}
      {columns.map((field) =>
        field.type === ApiFieldTypes.employee_compensation_lines ? (
          lines
        ) : (
          <div key={field.slug}>
            {employee &&
              getEmployeeEditFieldByType(field, employee, form, {
                forceCanView: true,
                forceCanEdit: true,
              })}
          </div>
        )
      )}
    </Stack>
  );
}
